import { FC, useCallback, useMemo } from 'react';
import { translateReference } from '../../../../../core/legacy/utils/translations';
import type { Reference, RoadData } from '../../../../../core/domain/Roaddata';
import { useAppDispatch, useAppSelector } from '../../../../../types';
import EditableSelect from '../../../../components/streetView/roadDatasPanel/EditableSelect';
import {
    sortedReferencesByRubricIdQuery,
    groupedReferencesByRubricIdQuery,
    rubricByIdQuery,
} from '../../../../../core/catalogs/queries';
import { updateRoadDataReference } from '../../../../../core/actions/streetView';

type Props = {
    attributeName?: string;
    reference: Reference | null;
    roadData: RoadData;
};

export const SubClassComp: FC<Props> = ({ attributeName, reference, roadData }) => {
    const dispatch = useAppDispatch();
    const sortedReferencesSelector = useMemo(
        () => sortedReferencesByRubricIdQuery(roadData.rubricId),
        [roadData.rubricId],
    );
    const groupedReferencesSelector = useMemo(
        () => groupedReferencesByRubricIdQuery(roadData.rubricId),
        [roadData.rubricId],
    );
    const references = useAppSelector(sortedReferencesSelector);
    const grouped = useAppSelector(groupedReferencesSelector);
    const rubric = useAppSelector(rubricByIdQuery(roadData.rubricId));
    const isSV = rubric?.code === 'SV';
    const findReference = useCallback((id?: string) => references.find((option) => option.id === id), [references]);
    const currentReference = findReference(roadData.reference?.id);

    const save = useCallback(
        async (refId: string) => {
            await dispatch(updateRoadDataReference(roadData, findReference(refId)));
        },
        [roadData, dispatch],
    );

    const { groups, standaloneRefs } = useMemo(() => {
        const realGroups = grouped.filter((g) => g.children.length > 0);
        const standalone = grouped.filter((g) => g.children.length === 0).map((g) => g.parent);

        if (realGroups.length === 0) {
            return { groups: undefined, standaloneRefs: standalone };
        }

        return {
            groups: realGroups.map((g) => ({
                groupLabel: g.parent.name,
                // SV parent categories are valid annotation values themselves; other rubrics' parents are grouping labels only
                parentOption: isSV ? { label: translateReference(g.parent), value: g.parent.id } : undefined,
                childOptions: g.children.map((child) => ({ label: translateReference(child), value: child.id })),
            })),
            standaloneRefs: standalone,
        };
    }, [grouped, isSV]);

    return (
        <EditableSelect
            attributeName={attributeName}
            initialValue={currentReference?.id || ''}
            onSave={save}
            options={standaloneRefs.map((ref) => ({ label: translateReference(ref), value: ref.id }))}
            groups={groups}
            roadDataId={roadData.id}
            title={{ value: translateReference(reference ?? undefined) }}
        />
    );
};
